import React, { useEffect, useMemo, useRef, useState } from 'react';
import "./FoodRequest.css";
import PrevIcon from "../../../assets/icons/PrevIcon.svg";
import SearchIcon from "../../../assets/icons/Search.svg";
import FoodCard from './FoodCard/FoodCard';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuth from '../../../customHooks/useAuth';
import CloseIcon from "../../../assets/icons/closeIcon.svg"
import Photo from "../../../assets/photo.png";
import menuIcon from "../../../assets/icons/menu.png";
import MenuModal from './MenuModal/MenuModal';
import FoodTypeMark from './FoodTypeMark';
import {
    DIET_FILTERS,
    titleCase,
    orderedTypes,
} from '../../../common/foodMenu';

export default function FoodRequest() {

    const navigate = useNavigate();
    const location = useLocation();

    // Refresh Handle
    useAuth();

    const menu = location.state?.menu || [];
    const bookedRooms = location.state?.bookedRooms || [];
    const foodCartItems = location.state?.foodCart || [];

    const bookingId = location.state?.bookingId || "";
    const hotelId = location.state?.hotelId || "";
    const hotelAddress = location.state?.hotelAddress || "";
    const hotelName = location.state?.hotelName || '';
    const checkInDate = location.state?.checkInDate || "";
    const checkOutDate = location.state?.checkOutDate || "";
    const status = location.state?.status || "";
    const activeTab = location.state?.activeTab || "";

    const [isSearching, setIsSearching] = useState(false);
    const [search, setSearch] = useState("");
    const [diets, setDiets] = useState([]);
    const [onlyBestseller, setOnlyBestseller] = useState(false);
    const [onlyChefSpecial, setOnlyChefSpecial] = useState(false);
    const [activeType, setActiveType] = useState("");
    const [menuModal, setMenuModal] = useState(false);

    const [foodCart, setFoodCart] = useState([]);
    const groupRefs = useRef({});

    // CheckIds
    useEffect(() => {
        const checkId = () => {
            if (bookingId === "" || hotelId === "") {
                navigate("/bookings");
            }
        }
        checkId();
    }, [bookingId, hotelId, navigate]);

    // Set FoodCartItems from location
    useEffect(() => {
        if (foodCartItems.length > 0) {
            setFoodCart(foodCartItems);
        }
    }, [foodCartItems]);

    const totalPrice = useMemo(
        () => foodCart.reduce((sum, food) => sum + food.price * food.quantity, 0),
        [foodCart],
    );

    const quantity = useMemo(
        () => foodCart.reduce((sum, food) => sum + food.quantity, 0),
        [foodCart],
    );

    const types = useMemo(() => orderedTypes(menu), [menu]);

    useEffect(() => {
        if (types.length > 0 && !types.includes(activeType)) {
            setActiveType(types[0]);
        }
    }, [types, activeType]);

    const toggleDiet = (key) => {
        setDiets(prev => prev.includes(key) ? prev.filter(d => d !== key) : [...prev, key]);
    };

    const clearFilters = () => {
        setDiets([]);
        setOnlyBestseller(false);
        setOnlyChefSpecial(false);
        setSearch("");
    };

    const filtersApplied =
        diets.length > 0 || onlyBestseller || onlyChefSpecial || search !== "";

    const matchesFilters = (food) => {
        if (diets.length > 0 && !diets.includes(food.food_type)) {
            return false;
        }
        if (onlyBestseller && !food.is_bestseller) {
            return false;
        }
        if (onlyChefSpecial && !food.is_chef_recommended) {
            return false;
        }
        if (search !== "" && !food.name?.toLowerCase().includes(search.toLowerCase())) {
            return false;
        }
        return true;
    };

    // Searching looks across the whole menu; browsing stays inside the cuisine
    // tab the guest picked.
    const visibleMenu = (menu || []).filter(food =>
        matchesFilters(food) && (search !== "" || (food.type || 'Other') === activeType)
    );

    const groups = useMemo(() => {
        const byType = {};
        visibleMenu.forEach(food => {
            const type = food.type || 'Other';
            if (!byType[type]) {
                byType[type] = [];
            }
            byType[type].push(food);
        });
        return Object.entries(byType);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menu, activeType, search, diets, onlyBestseller, onlyChefSpecial]);

    const scrollToType = (type) => {
        const match = types.find(
            candidate => candidate?.toLowerCase() === String(type || "").toLowerCase()
        );
        if (match) {
            setActiveType(match);
            setSearch("");
            setIsSearching(false);
        }

        setTimeout(() => {
            // The browse sheet upper-cases the type it hands back, so the group
            // is matched on case-insensitively rather than by exact key.
            const key = Object.keys(groupRefs.current).find(
                candidate => candidate?.toLowerCase() === String(type || "").toLowerCase()
            );
            const target = key ? groupRefs.current[key] : null;
            if (target) {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };

    const handleConfirm = () => {
        navigate("/foodSummary", {
            state: {
                menu: menu,
                foodCart: foodCart,
                totalPrice: totalPrice,
                bookedRooms: bookedRooms,
                bookingId: bookingId,
                hotelId: hotelId,
                hotelAddress: hotelAddress,
                hotelName: hotelName,
                checkInDate: checkInDate,
                checkOutDate: checkOutDate,
                status: status,
                activeTab: activeTab,
            }
        });
    }

    const goBack = () => {
        navigate(`/bookings/${bookingId}`);
    }

    return (
        <>
            <div className="foodRequest">

                {/* Header */}
                <div className="foodRequest-header">
                    <div className="foodRequest-topbar">
                        <img src={PrevIcon} alt="Back" onClick={goBack} className="foodRequest-back" />
                        {isSearching ? (
                            <>
                                <input
                                    type="text"
                                    placeholder="Search dishes..."
                                    className="searchField"
                                    autoFocus
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                                <img
                                    src={CloseIcon}
                                    alt="Close search"
                                    className="foodRequest-search-icon"
                                    onClick={() => { setSearch(""); setIsSearching(false); }}
                                />
                            </>
                        ) : (
                            <>
                                <div className="foodRequest-title">
                                    <h2>Food Menu</h2>
                                    {hotelName && <p>{hotelName}</p>}
                                </div>
                                <img
                                    src={SearchIcon}
                                    alt="Search"
                                    className="foodRequest-search-icon"
                                    onClick={() => setIsSearching(true)}
                                />
                            </>
                        )}
                    </div>

                    {/* Cuisine tabs */}
                    {search === "" && types.length > 1 && (
                        <div className="food-tabs">
                            {types.map(type => (
                                <button
                                    key={type}
                                    className={`food-tab ${activeType === type ? 'active' : ''}`}
                                    onClick={() => setActiveType(type)}
                                >
                                    {titleCase(type)}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Filters */}
                    <div className="food-filters">
                        {DIET_FILTERS.map(filter => (
                            <button
                                key={filter.key}
                                className={`food-chip ${diets.includes(filter.key) ? 'active' : ''}`}
                                onClick={() => toggleDiet(filter.key)}
                            >
                                <FoodTypeMark foodType={filter.key} size={12} />
                                {filter.label}
                            </button>
                        ))}
                        <button
                            className={`food-chip ${onlyBestseller ? 'active' : ''}`}
                            onClick={() => setOnlyBestseller(prev => !prev)}
                        >
                            ⭐ Bestseller
                        </button>
                        <button
                            className={`food-chip ${onlyChefSpecial ? 'active' : ''}`}
                            onClick={() => setOnlyChefSpecial(prev => !prev)}
                        >
                            👨‍🍳 Chef's Special
                        </button>
                        {filtersApplied && (
                            <button className="food-chip food-chip-clear" onClick={clearFilters}>
                                Clear
                            </button>
                        )}
                    </div>

                </div>

                {/* Menu */}
                <div className="food-menu">
                    {groups.length === 0 && (
                        <div className="food-empty">
                            <p>No dishes match what you're looking for.</p>
                            {filtersApplied && (
                                <button onClick={clearFilters}>Clear filters</button>
                            )}
                        </div>
                    )}

                    {groups.map(([type, items]) => (
                        <div
                            className="food-group"
                            key={type}
                            ref={(node) => { groupRefs.current[type] = node; }}
                        >
                            {search !== "" && (
                                <div className="food-group-head">
                                    <h3>{titleCase(type)}</h3>
                                    <span>{items.length}</span>
                                </div>
                            )}

                            {items.map((food) => (
                                <FoodCard
                                    key={food.id}
                                    name={food.name}
                                    price={food.price}
                                    desc={food.description}
                                    is_bestseller={food.is_bestseller}
                                    photo={food.photo && food.photo !== "" ? food.photo : Photo}
                                    id={food.id}
                                    foodCart={foodCart}
                                    setFoodCart={setFoodCart}
                                    is_chef_recommended={food.is_chef_recommended}
                                    average_rating={food.average_rating}
                                    number_of_ratings={food.number_of_ratings}
                                    is_available={food?.is_available}
                                    availability_windows={food?.availability_windows}
                                    gst_percentage={food?.gst_percentage}
                                    food_type={food.food_type}
                                />
                            ))}
                        </div>
                    ))}
                </div>

                {/* Browse categories */}
                <button
                    className={`food-categories-fab ${foodCart.length > 0 ? 'raised' : ''} ${menuModal ? 'open' : ''}`}
                    onClick={() => setMenuModal(prev => !prev)}
                >
                    <img src={menuIcon} alt="" />
                    {menuModal ? "Close" : "Categories"}
                </button>

                {/* Cart — floats over the menu, only once there is something in it */}
                {foodCart.length > 0 && (
                    <div className="food-cart-bar">
                        <div className="food-cart-info">
                            <h4>₹ {totalPrice}</h4>
                            <p>{quantity} {quantity === 1 ? "item" : "items"} added</p>
                        </div>
                        <button className="food-cart-cta" onClick={handleConfirm}>
                            Confirm Order
                        </button>
                    </div>
                )}

            </div>

            {menuModal && (
                <MenuModal
                    setMenuModal={setMenuModal}
                    menu={menu}
                    onSelectType={scrollToType}
                    activeType={activeType}
                    raised={foodCart.length > 0}
                />
            )}
        </>
    )
}
